// ═══════════════════════════════════════════════════════════════
// GIVING SNAPSHOT WIDGET
// Path: src/components/dashboard/GivingSnapshot.jsx
//
// 4 KPI tiles: Gifts This Month, This Quarter, This Year, Total Donors
// Fetches from gifts table on mount. Self-contained.
// ═══════════════════════════════════════════════════════════════

import React, { useMemo } from "react";
import { DollarSign, TrendingUp, Calendar, Users } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { getFiscalDates } from "../../utils/fiscalYear";

const formatCurrency = (amount) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

export const GivingSnapshot = () => {
  const { palette, members, chapterSettings, allGifts } = useApp();

  const fiscalStartMonth = chapterSettings?.fiscalYearStartMonth || 1;

  const stats = useMemo(() => {
    const fiscal = getFiscalDates(fiscalStartMonth);
    let monthTotal = 0, monthCount = 0;
    let quarterTotal = 0, quarterCount = 0;
    let yearTotal = 0, yearCount = 0;

    allGifts.forEach((g) => {
      if (g.gift_date < fiscal.yearStart) return;
      const amt = parseFloat(g.amount) || 0;
      yearTotal += amt;
      yearCount++;
      if (g.gift_date >= fiscal.quarterStart) {
        quarterTotal += amt;
        quarterCount++;
      }
      if (g.gift_date >= fiscal.monthStart) {
        monthTotal += amt;
        monthCount++;
      }
    });

    return { monthTotal, monthCount, quarterTotal, quarterCount, yearTotal, yearCount, yearLabel: fiscal.label };
  }, [allGifts, fiscalStartMonth]);

  const donorCount = members.filter((m) => m.isDonor).length;

  const tiles = [
    {
      label: "This Month",
      value: formatCurrency(stats.monthTotal),
      sub: `${stats.monthCount} gift${stats.monthCount !== 1 ? "s" : ""}`,
      icon: Calendar,
      color: "#3b82f6",
    },
    {
      label: "This Quarter",
      value: formatCurrency(stats.quarterTotal),
      sub: `${stats.quarterCount} gift${stats.quarterCount !== 1 ? "s" : ""}`,
      icon: TrendingUp,
      color: "#10b981",
    },
    {
      label: stats.yearLabel || "This Year",
      value: formatCurrency(stats.yearTotal),
      sub: `${stats.yearCount} gift${stats.yearCount !== 1 ? "s" : ""}`,
      icon: DollarSign,
      color: "#f59e0b",
    },
    {
      label: "Active Donors",
      value: donorCount,
      sub: `of ${members.length} total`,
      icon: Users,
      color: "#8b5cf6",
    },
  ];

  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
        gap: 12,
      }}
    >
      {tiles.map((tile) => (
        <div
          key={tile.label}
          style={{
            background: palette.cardBg,
            border: `1px solid ${palette.cardBorder}`,
            borderRadius: 10,
            padding: "16px 18px",
            display: "flex",
            flexDirection: "column",
            gap: 4,
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span
              style={{
                fontSize: "0.65rem",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                color: palette.text3,
                fontFamily: FONT_FAMILY,
              }}
            >
              {tile.label}
            </span>
            <tile.icon size={ICON_SIZES.sm} style={{ color: tile.color, opacity: 0.7 }} />
          </div>
          <div
            style={{
              fontSize: FONT_SIZES.xl,
              fontWeight: 700,
              color: palette.text1,
              fontFamily: FONT_FAMILY,
            }}
          >
            {tile.value}
          </div>
          <div
            style={{
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              fontFamily: FONT_FAMILY,
            }}
          >
            {tile.sub}
          </div>
        </div>
      ))}
    </div>
  );
};
